import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatErr, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";

const EMPTY = { code: "", event_id: "all", type: "percent", value: 10, max_uses: "", expires_at: "" };

const PromoForm = ({ events, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { code: f.code, type: f.type, value: Number(f.value), event_id: f.event_id === "all" ? null : f.event_id,
        max_uses: f.max_uses ? Number(f.max_uses) : null, expires_at: f.expires_at || null };
      onCreated((await api.post("/photographer/promos", body)).data);
      setF(EMPTY);
      toast.success("Kode promo dibuat");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2"><Label>Kode promo</Label><Input required placeholder="MIS. LARI20" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} className="font-mono-x uppercase" data-testid="promo-code-input" /></div>
      <div className="space-y-2">
        <Label>Berlaku untuk</Label>
        <Select value={f.event_id} onValueChange={(v) => setF({ ...f, event_id: v })}>
          <SelectTrigger data-testid="promo-event-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua event saya</SelectItem>
            {events.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`promo-event-option-${e.id}`}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Jenis diskon</Label>
          <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
            <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent" data-testid="promo-type-percent">Persen (%)</SelectItem>
              <SelectItem value="fixed" data-testid="promo-type-fixed">Potongan (Rp)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>{f.type === "percent" ? "Diskon (%)" : "Potongan (Rp)"}</Label><Input type="number" min={1} max={f.type === "percent" ? 100 : undefined} required value={f.value} onChange={set("value")} data-testid="promo-value-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Kuota pemakaian</Label><Input type="number" min={1} placeholder="Tanpa batas" value={f.max_uses} onChange={set("max_uses")} data-testid="promo-max-uses-input" /></div>
        <div className="space-y-2"><Label>Berlaku sampai</Label><Input type="date" value={f.expires_at} onChange={set("expires_at")} data-testid="promo-expires-input" /></div>
      </div>
      <p className="text-xs text-zinc-500">Setiap member hanya bisa memakai satu kode sekali. Diskon hanya berlaku untuk foto milikmu di event terpilih.</p>
      <Button className="w-full rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="promo-submit-button">Buat kode</Button>
    </form>
  );
};

export default function PhotographerPromos() {
  const { user } = useAuth();
  const [promos, setPromos] = useState([]);
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/photographer/promos").then((r) => setPromos(r.data));
    api.get("/events", { params: { photographer_id: user.id } }).then((r) => setEvents(r.data));
  }, [user.id]);

  const toggle = async (p, active) => {
    try {
      const { data } = await api.patch(`/photographer/promos/${p.id}`, { active });
      setPromos((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Diskon untuk peserta" title="Kode Promo">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11" data-testid="create-promo-button"><Plus className="mr-2 h-4 w-4" />Kode baru</Button></DialogTrigger>
          <DialogContent className="rounded-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle>Buat kode promo</DialogTitle></DialogHeader>
            <PromoForm events={events} onCreated={(p) => { setPromos((prev) => [p, ...prev]); setOpen(false); }} />
          </DialogContent>
        </Dialog>
      </PageHeader>
      <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
        <Table data-testid="promo-table">
          <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Event</TableHead><TableHead>Diskon</TableHead><TableHead>Dipakai</TableHead><TableHead>Berlaku sampai</TableHead><TableHead className="text-right">Aktif</TableHead></TableRow></TableHeader>
          <TableBody>
            {promos.map((p) => (
              <TableRow key={p.id} data-testid={`promo-row-${p.id}`}>
                <TableCell className="font-mono-x font-semibold">{p.code}</TableCell>
                <TableCell>{p.event_title || "Semua event"}</TableCell>
                <TableCell>{p.type === "percent" ? `${p.value}%` : rupiah(p.value)}</TableCell>
                <TableCell data-testid={`promo-used-${p.id}`}>{p.used_count}{p.max_uses ? ` / ${p.max_uses}` : ""}</TableCell>
                <TableCell>{p.expires_at || "-"}</TableCell>
                <TableCell className="text-right"><Switch checked={p.active} onCheckedChange={(v) => toggle(p, v)} data-testid={`promo-toggle-${p.id}`} /></TableCell>
              </TableRow>
            ))}
            {promos.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-12">Belum ada kode promo.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </main>
  );
}
